import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { KeyingAppEnvironmentConfig } from '../configuration/keying.app.environment.config'
import { LoadingService } from './loading.service'
import { ErrorService } from './error.service'
import { forkJoin, take, BehaviorSubject, finalize } from 'rxjs'
import { TokenizedMessageLoadingService } from './tokenized-message-loading/tokenized-message-loading.service'
import { FormatDataService } from './format.data.service'
import { ApiService } from '@overtur-monorepo/keying-shared'
import { KeywayService, MasterKey } from '@overtur-monorepo/keying-hierarchy'
import { Assignment } from 'libs/keying-hierarchy/src/lib/models/keying-models'
import { KeyingLibraryService } from './keying.library.service'
import { Book, MoveBittingsRequest, BittingDataSets, BittingSearchResult } from '../models/dev.test.models'
import { LoggingService } from './logging.service'

@Injectable({
  providedIn: 'root',
})
export class KeyingBookService {
  bittingDataSets = new BehaviorSubject<BittingDataSets>(null)
  bittingDataSets$ = this.bittingDataSets.asObservable()

  constructor(
    @Inject(HttpClient) private httpClient: HttpClient,
    private keyingAppEnvironmentConfig: KeyingAppEnvironmentConfig,
    private loadingService: LoadingService,
    private errorService: ErrorService,
    private formatDataService: FormatDataService,
    private keywayService: KeywayService,
    private api: ApiService,
    private keyingLibraryService: KeyingLibraryService,
    private loggingService: LoggingService,
    private tokenizedMessageLoadingService: TokenizedMessageLoadingService
  ) {}

  getMatrix(keySystemId, masterKeyId) {
    // this.loadingService.set({ isLoading: true, message: 'Getting Matrix' })

    let headers = new HttpHeaders().set('Accept', 'application/allegion.keying.get.combination.attributes+json')

    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/keySystems/${keySystemId}/MasterKeys/${masterKeyId}/Combinations`
    return this.httpClient.get<any[]>(url, { headers })
  }

  getCombinations(keySystemId, masterKeyId) {
    // this.loadingService.set({ isLoading: true, message: 'Getting Books' });

    let headers = new HttpHeaders().set('Accept', 'application/allegion.keying.combinations+json')

    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/keySystems/${keySystemId}/MasterKeys/${masterKeyId}/Combinations`
    return this.httpClient.get<any[]>(url, { headers })
  }

  SaveBook(keySystemId: number, keyId: number, book: unknown) {
    const headers = new HttpHeaders().set('Content-type', 'application/allegion.keying.create.book+json')
    const serviceEndpoint = `keySystems/${keySystemId}/Keys/${keyId}/Books`
    return this.api.post<any, any>(this.buildUrl(serviceEndpoint), book, headers)
  }

  updateBook(keySystemId: number, keyId: number, bookId: number, combinationsAssginedBittings) {
    const serviceEndpoint = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/keySystems/${keySystemId}/Keys/${keyId}/Books/${bookId}`
    return this.httpClient.put<any>(serviceEndpoint, combinationsAssginedBittings)
  }

  bittingSplit(keySystemId: number, keyId: number, bookId: number, request) {
    let headers = new HttpHeaders().set('Content-type', 'application/allegion.keying.splitbitting+json')

    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/keySystems/${keySystemId}/Keys/${keyId}/Books/${bookId}`
    return this.httpClient.patch<any>(url, request, { headers })
  }

  undoBittingSplit(keySystemId: number, keyId: number, bookId: number, request) {
    let headers = new HttpHeaders().set('Content-type', 'application/allegion.keying.undosplitbitting+json')

    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/keySystems/${keySystemId}/Keys/${keyId}/Books/${bookId}`
    return this.httpClient.patch<any>(url, request, { headers })
  }

  bittingsCombine(keySystemId: number, keyId: number, bookId: number, request) {
    // CombineType : 1 is Block Level
    // CombineType : 2 is Column Level
    // CombineType : 3 is Line Master

    let headers = new HttpHeaders().set('Content-type', 'application/allegion.keying.combinebittings+json')

    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/keySystems/${keySystemId}/Keys/${keyId}/Books/${bookId}`
    return this.httpClient.patch<any>(url, request, { headers })
  }

  undoBittingsCombine(keySystemId: number, keyId: number, bookId: number, request) {
    let headers = new HttpHeaders().set('Content-type', 'application/allegion.keying.undocombine+json')

    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/keySystems/${keySystemId}/Keys/${keyId}/Books/${bookId}`
    return this.httpClient.patch<any>(url, request, { headers })
  }

  deleteBook(keySystemId, keyId, bookId, auditTrailRequest: any = undefined) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/keySystems/${keySystemId}/Keys/${keyId}/Books/${bookId}`
    return this.httpClient.delete<any[]>(url, { body: { auditTrailRequest: auditTrailRequest || {} } })
  }

  findBitting(keySystemId: number, keyId: number, bitting: string) {
    const headers = new HttpHeaders().set('Accept', 'application/allegion.keying.find.bitting+json')
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/keySystems/${keySystemId}/Keys/${keyId}/Books/Bitting/${bitting}`
    return this.httpClient.get<BittingSearchResult[]>(url, { headers })
  }

  // Move bittings to a new book (POST)
  moveBittingsToNewBook(keySystemId: number, keyId: number, request: MoveBittingsRequest) {
    const token = this.tokenizedMessageLoadingService.startLoading('Moving Bittings', 'move-bittings-new', 'medium')
    const headers = new HttpHeaders().set('Content-type', 'application/allegion.keying.move.bittings+json')
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/keySystems/${keySystemId}/Keys/${keyId}/Books`
    return this.httpClient.post<any>(url, request, { headers }).pipe(
      finalize(() => this.tokenizedMessageLoadingService.stopLoading(token))
    )
  }

  // Move bittings to an existing book (PUT)
  moveBittingsToExistingBook(keySystemId: number, keyId: number, bookId: number, request: MoveBittingsRequest) {
    const token = this.tokenizedMessageLoadingService.startLoading('Moving Bittings', 'move-bittings-existing', 'medium')
    const headers = new HttpHeaders().set('Content-type', 'application/allegion.keying.move.bittings+json')
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/keySystems/${keySystemId}/Keys/${keyId}/Books/${bookId}`
    return this.httpClient.put<any>(url, request, { headers }).pipe(
      finalize(() => this.tokenizedMessageLoadingService.stopLoading(token))
    )
  }

  CalculateCombinations(keySystemId: number, masterKeyId: number, book: any) {
    const headers = new HttpHeaders().set('Content-type', 'application/allegion.keying.calculatecombinations+json')
    const serviceEndpoint = `keySystems/${keySystemId}/MasterKeys/${masterKeyId}/Combinations`
    return this.api.post<any, any>(this.buildUrl(serviceEndpoint), book, headers)
  }

  putMatrix(keySystemId, masterKeyId, matrix) {
    let headers = new HttpHeaders().set('Content-type', 'application/allegion.keying.put.combination.attributes+json')

    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/keySystems/${keySystemId}/MasterKeys/${masterKeyId}/Combinations`
    return this.httpClient.put<any[]>(url, matrix, { headers })
  }

  applyLocalAssignments(resData, localAssignments, selectedBook, tmk: MasterKey) {
    // console.log('resData', resData)
    // console.log('localAssignments', localAssignments)

    let subject = resData[1].find((book) => book.id === selectedBook.id)
    let flat = tmk.squashIncludeChangeKeys()

    subject['pages'].map((page) => {
      page.columns.map((column) => {
        column.blocks.map((block) => {
          block.bittings.map((bitting) => {
            let foundAssignment = localAssignments.find((assignment) => assignment.bitting === bitting.masterKeyBitting)

            if (foundAssignment && foundAssignment.masterKeyId) {
              bitting.assignedMasterKeyId = foundAssignment.masterKeyId
              let mk = flat.find((mk) => mk.id === foundAssignment.masterKeyId)
              mk.isAssignedLocally = true
              mk.unassignedLocally = false
              mk.assignment = new Assignment()
              mk.assignment.kba = bitting
            }
            if (foundAssignment && foundAssignment.CombinationBittingReservationTypeId) {
              bitting.combinationBittingReservationTypeId = foundAssignment.CombinationBittingReservationTypeId
              bitting.combinationBittingReservationType = { description: foundAssignment.description }
              bitting.combinationBittingReservationNotes = foundAssignment.CombinationBittingReservationNotes
            }
          })
        })
      })
    })

    subject['splitBittings'].map((bitting) => {
      let foundAssignment = localAssignments.find((assignment) => assignment.bitting === bitting.masterKeyBitting)

      if (foundAssignment && foundAssignment.masterKeyId) {
        bitting.assignedMasterKeyId = foundAssignment.masterKeyId
        let mk = flat.find((mk) => mk.id === foundAssignment.masterKeyId)
        mk.isAssignedLocally = true
        mk.unassignedLocally = false
        mk.assignment = new Assignment()
        mk.assignment.kba = bitting
      }
      if (foundAssignment && foundAssignment.CombinationBittingReservationTypeId) {
        bitting.combinationBittingReservationTypeId = foundAssignment.CombinationBittingReservationTypeId
        bitting.combinationBittingReservationType = { description: foundAssignment.description }
        bitting.combinationBittingReservationNotes = foundAssignment.CombinationBittingReservationNotes
      }
    })

    subject['combineBittings'].map((bitting) => {
      let foundAssignment = localAssignments.find((assignment) => assignment.bitting === bitting.masterKeyBitting)

      if (foundAssignment && foundAssignment.masterKeyId) {
        bitting.assignedMasterKeyId = foundAssignment.masterKeyId
        let mk = flat.find((mk) => mk.id === foundAssignment.masterKeyId)
        mk.isAssignedLocally = true
        mk.unassignedLocally = false
        mk.assignment = new Assignment()
        mk.assignment.kba = bitting
      }
      if (foundAssignment && foundAssignment.CombinationBittingReservationTypeId) {
        bitting.combinationBittingReservationTypeId = foundAssignment.CombinationBittingReservationTypeId
        bitting.combinationBittingReservationType = { description: foundAssignment.description }
        bitting.combinationBittingReservationNotes = foundAssignment.CombinationBittingReservationNotes
      }
    })
    // console.log('subject', subject)
  }

  loadFormattedCombinations(
    structure,
    keySystemId,
    masterKey,
    localAssignments?,
    selectedBook?,
    tmk?,
    selectLastBook = false
  ) {
    this.loadingService.set({ isLoading: true, message: 'Loading Books' })
    forkJoin([this.getMatrix(keySystemId, masterKey.id), this.getCombinations(keySystemId, masterKey.id)])
      .pipe(take(1))
      .subscribe(
        (resData) => {
          this.loggingService.log('Bitting Data Returned', resData)
          const newData = new BittingDataSets()

          if (localAssignments && selectedBook && tmk) {
            this.applyLocalAssignments(resData, localAssignments, selectedBook, tmk)
          }

          newData.matrix = this.formatDataService.formatMatrix(resData[0])
          newData.library = this.formatDataService.formatLibrary(resData[1], masterKey, true)

          this.keywayService
            .getAllKeyways(structure.keywayFamilyId)
            .pipe(take(1))
            .subscribe((keyways) => {
              // Set KeyWays so other components/services could consume, to avoid duplicate API calls
              this.keywayService.setAllKeyways(keyways)

              newData.library.books.map((book) => {
                if (book.keywayId) {
                  let keyway = keyways.find((kw) => kw.id === book.keywayId)
                  book.keySectionName = keyway.name
                }
              })

              this.bittingDataSets.next(newData)
              if (selectLastBook) {
                // 1. Find the last book in the library
                let book = newData.library.books[newData.library.books.length - 1]
                // 2. Select the last book using the library service
                this.keyingLibraryService.selectBook(book)
              }
              this.loadingService.set({ isLoading: false, message: '' })
            })
        },
        (error) => {
          this.errorService.throw(error)
          this.loadingService.set({ isLoading: false, message: '' })
        }
      )
  }

  private buildUrl(serviceEndpoint: string): string {
    return `${this.keyingAppEnvironmentConfig.baseUrl}/${serviceEndpoint}`
  }
}
